import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/")({
  component: Index,
  head: () => ({
    title: "Pristine Interiors | Luxury Design & Remodeling",
    meta: [
      { name: "description", content: "Pristine Interiors specializes in Dallas luxury home renovations, kitchen and bathroom remodeling, and whole house design." },
      { property: "og:title", content: "Pristine Interiors | Luxury Design & Remodeling" },
      { property: "og:description", content: "Specializing in luxury home renovations across Dallas-Fort Worth." },
    ],
  }),
});

function Index() {
  return (
    <div className="min-h-screen bg-[#fff] text-[#60524C] font-['Montserrat',sans-serif]">
      {/* Cookie Notice */}
      <div className="fixed bottom-0 left-0 right-0 z-[100] bg-[#fff] border-t border-[#60524C]/20 text-[#60524C] py-4 px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] tracking-wider uppercase font-bold">
        <span>This website uses cookies to ensure you get the best experience</span>
        <button className="border border-[#60524C] px-4 py-1 hover:bg-[#60524C] hover:text-white transition-colors">Accept</button>
      </div>

      {/* Navigation */}
      <header className="fixed top-0 w-full z-50 bg-white border-b border-gray-100 px-6 lg:px-24 py-6 flex justify-between items-center shadow-sm">
        <div className="flex items-center space-x-2">
          <div className="border-2 border-[#60524C] p-1">
            <div className="text-lg lg:text-xl font-black tracking-tight uppercase text-[#60524C] leading-none text-center">
              PRISTINE INTERIORS<br/><span className="text-[8px] tracking-[0.4em] font-normal">DESIGN + BUILD</span>
            </div>
          </div>
        </div>
        <nav className="hidden md:flex items-center space-x-10 text-[11px] tracking-[0.2em] uppercase font-bold text-[#60524C]">
          <a href="#" className="hover:text-[#97afd4] transition-colors">Home</a>
          <div className="group relative">
            <a href="#" className="hover:text-[#97afd4] transition-colors flex items-center gap-1">Services <span className="text-[8px]">▼</span></a>
          </div>
          <a href="#" className="hover:text-[#97afd4] transition-colors">Projects</a>
          <a href="#" className="hover:text-[#97afd4] transition-colors">About</a>
          <a href="#" className="border-2 border-[#60524C] px-6 py-2 hover:bg-[#60524C] hover:text-white transition-all">GET STARTED</a>
        </nav>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="relative h-[90vh] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img 
              src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80" 
              className="w-full h-full object-cover"
              alt="Luxury Dallas Home Interior Design"
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </div>
          
          <div className="relative z-10 text-center text-white px-4 max-w-5xl">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold uppercase tracking-widest mb-10 leading-tight">
              PRISTINE INTERIORS <br/>
              <span className="text-2xl md:text-3xl block mt-4 font-normal tracking-[0.4em] opacity-80">BY EVA</span>
            </h1>
            <button className="border-2 border-white text-white px-10 py-4 text-[12px] tracking-[0.3em] uppercase font-bold hover:bg-white hover:text-black transition-all">
              Book a Consultation
            </button>
          </div>
        </section>

        {/* About Section - Split Layout Matching Image */}
        <section className="py-24 px-6 lg:px-24 bg-white overflow-hidden">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-3/5 space-y-8 pr-0 lg:pr-12">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <span className="text-[11px] tracking-[0.4em] uppercase font-bold text-[#60524C]/60">ABOUT</span>
                  <div className="h-[2px] w-32 bg-[#97afd4]"></div>
                </div>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal uppercase tracking-tight leading-none text-black">
                  REDEFINING <span className="font-black">LUXURY HOME</span>
                </h2>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal uppercase tracking-tight leading-none text-black">
                  <span className="font-black">RENOVATION</span> IN DFW
                </h2>
              </div>
              
              <div className="space-y-6 text-[#60524C]/80 leading-relaxed text-sm lg:text-base font-light max-w-xl">
                <p>
                  <span className="font-bold uppercase tracking-widest text-[10px] block mb-2">PRISTINE INTERIORS</span>
                  Pristine Interiors Design + Build is a premier General Contracting firm specializing in luxury new home construction and high-end renovations throughout the Dallas-Fort Worth (DFW) Area.
                </p>
                <p>
                  At <span className="font-bold">PRISTINE INTERIORS</span>, we are dedicated to delivering a seamless, stress-free renovation experience—guiding clients through every phase, from initial design concepts to final construction.
                </p>
                <p>
                  As a leading <span className="font-bold">Design + Build</span> firm in Dallas, Texas, we bring expertise and craftsmanship to every project, including custom new home construction, large-scale renovations and additions, as well as kitchen and bath transformations.
                </p>
                <p>
                  Our skilled in-house team meticulously oversees every aspect of the <span className="font-bold">Design + Build</span> process, ensuring each project is executed with precision, artistry, and an unwavering
                </p>
              </div>
            </div>
            
            <div className="w-full lg:w-2/5">
              <div className="aspect-[3/4] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80" 
                  className="w-full h-full object-cover shadow-2xl"
                  alt="Modern Luxury Interior"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Brand Statement / Expertise Intro */}
        <section className="py-24 px-6 lg:px-24 bg-white">
          <div className="max-w-4xl mx-auto space-y-12 text-center">
            <div className="space-y-6">
              <p className="text-xl md:text-2xl leading-relaxed font-light italic text-[#60524C]">
                "At Pristine Interiors, we believe your home should be more than just beautiful — it should be a true reflection of you and the life you want to live."
              </p>
              <p className="text-[#60524C]/80 leading-relaxed text-lg">
                With over 15 years of experience in interior design and general contracting, we specialize in helping Dallas homeowners reimagine their spaces — from full gut renovations to single-room makeovers.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-12 text-left pt-8 border-t border-gray-100">
              <p className="text-[#60524C]/80 leading-relaxed">
                Our projects range from a simple kitchen cabinet replacement to an entire house design and renovation. We don’t just design a vision and walk away — we manage every detail, from concept to completion, ensuring your renovation is seamless, stress-free, and executed to the highest standards.
              </p>
              <p className="text-[#60524C]/80 leading-relaxed">
                Our mission is to help you achieve a pulled-together look and feel for your home that is warm, inviting, and practical while reflecting your personality and lifestyle.
              </p>
            </div>
          </div>
        </section>

        {/* Services Section with Overlapping Text */}
        <section className="bg-black text-white relative overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-40">
            <img 
              src="https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80" 
              className="w-full h-full object-cover"
              alt="Services Background"
            />
          </div>
          <div className="max-w-7xl mx-auto px-6 lg:px-24 py-32 relative z-10 grid lg:grid-cols-2 gap-16">
            <div className="space-y-6">
              <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#97afd4] block">SERVICES</span>
              <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight">
                DESIGN-BUILD <span style={{ color: '#97afd4' }}>SERVICES</span> IN DALLAS, TX
              </h2>
            </div>
            <div className="space-y-6 text-gray-300 leading-relaxed text-sm lg:text-base">
              <p>
                At <span className="font-bold text-white">PRISTINE INTERIORS</span> in Dallas, Texas, we offer a distinct advantage through our <span className="font-bold text-white">Design + Build</span> services—an integrated approach that seamlessly merges the design and construction phases into a unified, efficient process. This ensures clear communication, enhanced collaboration, and streamlined project management from start to finish.
              </p>
              <p>
                By choosing <span className="font-bold text-white">PRISTINE INTERIORS</span> as your <span className="font-bold text-white">Design + Build</span> partner, you benefit from a single point of contact, simplifying communication and coordination. Our approach not only saves you time and money but also accelerates project completion while delivering exceptional craftsmanship and beautifully designed spaces that exceed expectations.
              </p>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto px-6 lg:px-24 pb-24 relative z-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {[
              { title: "New Construction", image: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80" },
              { title: "Home Renovations", image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80" },
              { title: "Kitchen Renovations", image: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80" },
              { title: "Bathroom Renovations", image: "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80" },
              { title: "Custom Projects", image: "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?auto=format&fit=crop&q=80" }
            ].map((s, i) => (
              <div key={i} className="aspect-[3/4] relative group overflow-hidden border border-white/20">
                <img src={s.image} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={s.title} />
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-colors"></div>
                <div className="absolute inset-0 flex items-center justify-center p-4 text-center">
                  <h3 className="text-sm font-bold uppercase tracking-widest leading-tight">{s.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Traditional Expertise Section (Renamed/Moved below) */}
        <section className="py-24 px-6 lg:px-24 bg-[#f9f9f9]">
          <div className="max-w-7xl mx-auto">
             <div className="text-center mb-16 space-y-4">
                <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#60524C]/60 block">PROJECTS</span>
                <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-[#60524C]">
                  REDEFINING <span style={{ color: '#97afd4' }}>LUXURY</span> IN NEW CONSTRUCTION AND HOME RENOVATIONS
                </h2>
                <p className="text-[#60524C]/60 text-sm lg:text-base max-w-3xl mx-auto italic">Discover our portfolio of stunning home transformations, showcasing exceptional design, meticulous detail, and unparalleled quality.</p>
             </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { 
                  title: "Full Home Renovations", 
                  image: "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&q=80",
                  desc: "From layout reconfiguration to finishes and furnishings, we manage complete gut renovations that bring new life and value to your home." 
                },
                { 
                  title: "Kitchen & Bathroom Remodels", 
                  image: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80",
                  desc: "We design and build spaces that are both beautiful and functional — tailored for how your family truly lives." 
                },
                { 
                  title: "Custom Design Solutions", 
                  image: "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?auto=format&fit=crop&q=80",
                  desc: "Built-ins, cabinetry, lighting, furniture sourcing, and finishing touches that make your home uniquely yours." 
                },
                { 
                  title: "Home Preparation For Sale", 
                  image: "https://images.unsplash.com/photo-1560448204-603b3fc33ddc?auto=format&fit=crop&q=80",
                  desc: "Add value to your home with our expertise in house preparation. Interior & exterior painting, new flooring or tile installation, deep cleaning." 
                },
                { 
                  title: "Design Consulting", 
                  image: "https://images.unsplash.com/photo-1616137422495-1e9e46e2aa77?auto=format&fit=crop&q=80",
                  desc: "Perfect for homeowners who need expert guidance with layouts, finishes, or color palettes. We provide professional direction so you can move forward with clarity and confidence." 
                },
                { 
                  title: "Small Projects, Done Right", 
                  image: "https://images.unsplash.com/photo-1616486029423-aaa4789e8c9a?auto=format&fit=crop&q=80",
                  desc: "Even the smallest remodel or refresh matters. We bring the same creativity and precision to projects of every size." 
                },
              ].map((s, i) => (
                <div key={i} className="bg-white shadow-sm border border-gray-100 hover:shadow-lg transition-all overflow-hidden group">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src={s.image} 
                      alt={s.title}
                      className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-110" 
                    />
                  </div>
                  <div className="p-8">
                    <h3 className="text-lg font-bold mb-4 text-[#60524C] uppercase tracking-widest">{s.title}</h3>
                    <p className="text-[#60524C]/70 leading-relaxed text-sm">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="text-center mt-16">
              <button className="bg-black text-white px-10 py-4 text-[12px] tracking-[0.3em] uppercase font-bold hover:bg-[#60524C] transition-all">
                SEE MORE PROJECTS
              </button>
            </div>
          </div>
        </section>

        {/* Trust Badge */}
        <section className="py-16 bg-[#fff] border-y border-gray-100 text-center px-6">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold tracking-[0.2em] uppercase text-[#60524C]">Pristine Home Interiors accredited by BBB</h3>
          </div>
        </section>

        {/* Our Approach Section - Matching Reference Image */}
        <section className="bg-black text-white py-24 px-6 lg:px-24">
          <div className="max-w-7xl mx-auto space-y-16">
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-white/60">OUR APPROACH</span>
                <div className="h-[2px] w-32 bg-[#97afd4]"></div>
              </div>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-tight max-w-2xl">
                WHAT IS THE <span className="text-white">DESIGN + BUILD PROCESS?</span>
              </h2>
            </div>
            
            <div className="relative">
              <div className="grid md:grid-cols-12 items-center">
                <div className="md:col-span-8">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img 
                      src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80" 
                      className="w-full h-full object-cover"
                      alt="Design Build Process"
                    />
                  </div>
                </div>
                
                <div className="md:col-span-6 md:absolute md:right-0 bg-white text-black p-10 lg:p-16 shadow-2xl space-y-8 max-w-xl z-10 mt-[-50px] md:mt-0">
                  <h3 className="text-xl lg:text-2xl font-normal uppercase tracking-widest leading-tight text-gray-800">
                    STREAMLINED, UNIFIED, EFFICIENT
                  </h3>
                  
                  <div className="space-y-6 text-gray-600 text-sm leading-relaxed font-light">
                    <p>
                      Traditionally, design and construction are managed separately, often requiring coordination between multiple entities.
                    </p>
                    <p>
                      At <span className="font-bold uppercase tracking-wider text-[11px]">Pristine Interiors</span> in Dallas, Texas, we take a different approach — integrating these crucial elements under one cohesive team through our <span className="font-bold">Design + Build</span> method.
                    </p>
                    <p>
                      This streamlined approach provides a single point of responsibility for your entire project, fostering seamless collaboration and efficient project management. By unifying the design and construction phases, we minimize risks, enhance communication, and optimize cost savings, ensuring a smoother, more efficient process from start to finish.
                    </p>
                  </div>
                  
                  <button className="bg-black text-white px-10 py-4 text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-[#60524C] transition-all flex items-center gap-2 group">
                    GET STARTED <span className="text-[14px] transition-transform group-hover:translate-x-1">›</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>


        {/* Original Mission Section (Kept as requested) */}
        <section className="py-24 bg-[#60524C] text-white px-6">
          <div className="max-w-5xl mx-auto text-center space-y-12">
            <div className="space-y-6 text-lg md:text-xl font-light leading-relaxed max-w-4xl mx-auto">
              <p>From furnishing, kitchen remodels to full-home renovations, we create spaces that reflect your personality, elevate your lifestyle, and bring comfort and beauty to everyday living.</p>
              <p>We take great pride in delivering exceptional customer service and limit the number of projects we take on each year to ensure that our clients receive the attention and time they deserve.</p>
              <p className="font-bold tracking-[0.2em] uppercase text-sm pt-4">Get in touch with us today</p>
            </div>
            <h2 className="text-3xl md:text-5xl font-bold uppercase tracking-widest leading-tight" style={{ color: '#97afd4' }}>
              READY TO TRANSFORM YOUR HOME? LET'S BEGIN YOUR DESIGN JOURNEY TOGETHER
            </h2>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-24 px-6 lg:px-24 bg-black text-white">
          <div className="max-w-7xl mx-auto text-center space-y-16">
            <div className="space-y-4">
              <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#97afd4] block">TESTIMONIALS</span>
              <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight leading-tight">
                SEE WHAT <span style={{ color: '#97afd4' }}>OUR CLIENTS ARE SAYING</span>
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  text: "Eva was invaluable in our renovation. She’s incredibly qualified & professional, resourceful & innovative, efficient & available. Her experience and vision bring such flexibility to the project that enables her & her team to meet you where you are and to take care of whatever needs you have or may encounter. From start to finish, we highly recommend Eva & her team.",
                  author: "Dr. David Whitten"
                },
                {
                  text: "I had a great experience with Eva. She has helped me to plan major house renovations. Picked up the right color of flooring, paint, furniture pieces, etc. Everything came out beautifully. Highly recommend her services.",
                  author: "Inessa M."
                },
                {
                  text: "Eva Priest and her team did an outstanding job renovating our living space and kitchen to match our vision perfectly. I shared my ideas on how I wanted the spaces to feel and the experiences I wanted to create for my family and friends to enjoy on any occasion. They transformed our home into a beautiful space that feels exactly how we wanted it to. Thank you!",
                  author: "Nicolas and Rachel"
                },
                {
                  text: "Pristine Interiors team did a fantastic job on the renovation of our kitchen from start to finish. They spent time to understand our needs and wants then executed a project that exceeded our expectations, on time and on budget too! Construction was done pretty quickly, then cabinets and appliances were installed, countertops, lights and all the details. We love our home now!",
                  author: "Jennifer K."
                },
                {
                  text: "My kitchen was completely gutted and Eva’s plan and design turned out amazing! It really opened it up. All of the furnishings look amazing that they picked for dining and living rooms. I feel like I have a brand new house.",
                  author: "Mark H."
                },
                {
                  text: "My husband and I have worked with Pristine Interiors on bathrooms in our house. The first was a complete renovation of our master bathroom and closets, then the other two small bathrooms. Work was done and it took about 8 weeks. During that whole time, Eva and her team coordinated every step and kept us updated on all phases of the project. We truly loved her design and would highly recommend her services.",
                  author: "Lisa & Greg Thomson"
                }
              ].map((t, i) => (
                <div key={i} className="space-y-6 text-left p-10 border border-white/10 bg-white/5 hover:bg-white/10 transition-colors">
                  <div className="flex gap-1 text-[#97afd4]">
                    {"★★★★★".split("").map((s, idx) => <span key={idx}>{s}</span>)}
                  </div>
                  <p className="italic text-gray-300 text-sm leading-relaxed">"{t.text}"</p>
                  <div className="pt-4 border-t border-white/10">
                    <p className="font-bold text-white uppercase text-[10px] tracking-[0.2em]">{t.author}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog Section */}
        <section className="py-24 px-6 lg:px-24 bg-[#f9f9f9]">
          <div className="max-w-7xl mx-auto space-y-16">
            <div className="text-center space-y-4">
              <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#60524C]/60 block">BLOG & NEWS</span>
              <h2 className="text-4xl md:text-5xl font-bold text-[#60524C] uppercase tracking-tight">STAY <span style={{ color: '#97afd4' }}>INSPIRED</span></h2>
            </div>
            <div className="grid md:grid-cols-2 gap-12">
              <div className="bg-transparent group overflow-hidden">
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1599619351208-3e6c839d6828?auto=format&fit=crop&q=80" 
                    alt="Kitchen Backsplash Trends"
                    className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-110"
                  />
                </div>
                <div className="p-8">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#97afd4] font-bold">Kitchen</span>
                  <h3 className="text-xl font-bold my-4 text-[#60524C] uppercase tracking-wide">7 Hot Trends for Your Kitchen Backsplash</h3>
                  <p className="text-[#60524C]/70 mb-6 leading-relaxed">This year's kitchen backsplash trends showcase a captivating blend of traditional and modern styles.</p>
                  <div className="flex justify-between items-center text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    <span>03.05.2024</span>
                    <a href="#" className="text-[#60524C] border-b border-[#60524C]">Read more</a>
                  </div>
                </div>
              </div>
              <div className="bg-transparent group overflow-hidden">
                <div className="aspect-[16/9] overflow-hidden">
                  <img 
                    src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80" 
                    alt="Kitchen Design Rules"
                    className="w-full h-full object-cover transition-all duration-700 scale-100 group-hover:scale-110"
                  />
                </div>
                <div className="p-8">
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#97afd4] font-bold">Kitchen</span>
                  <h3 className="text-xl font-bold my-4 text-[#60524C] uppercase tracking-wide">Kitchen Design Rules You Should Never Break</h3>
                  <p className="text-[#60524C]/70 mb-6 leading-relaxed">To create a functional, safe, and efficient kitchen, it is essential to adhere to the classic kitchen design rules that should never be broken.</p>
                  <div className="flex justify-between items-center text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                    <span>03.05.2024</span>
                    <a href="#" className="text-[#60524C] border-b border-[#60524C]">Read more</a>
                  </div>
                </div>
              </div>
            </div>
            <div className="text-center mt-12">
              <button className="text-[#60524C] font-bold uppercase text-[11px] tracking-widest border-b-2 border-[#60524C] pb-1 hover:text-[#97afd4] hover:border-[#97afd4] transition-all">See more</button>
            </div>
          </div>
        </section>

        {/* Featured In Placeholder */}
        <section className="py-16 bg-white border-y border-gray-100 text-center px-6">
          <div className="max-w-7xl mx-auto opacity-30 flex flex-wrap justify-center gap-12 grayscale">
            <div className="text-xl font-bold tracking-widest">FEATURED IN</div>
            <div className="text-xl font-bold tracking-widest">PRESS</div>
            <div className="text-xl font-bold tracking-widest">MAGAZINE</div>
            <div className="text-xl font-bold tracking-widest">JOURNAL</div>
          </div>
        </section>

        {/* FAQ Section Placeholder */}
        <section className="py-24 px-6 lg:px-24 bg-white">
          <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
            <div className="grid grid-cols-2 gap-4">
              <img src="https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80" className="w-full aspect-square object-cover" alt="Interior 1" />
              <img src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?auto=format&fit=crop&q=80" className="w-full aspect-square object-cover mt-8" alt="Interior 2" />
            </div>
            <div className="space-y-8">
               <div className="space-y-4">
                 <span className="text-[10px] tracking-[0.4em] uppercase font-bold text-[#60524C]/60 block">FAQS</span>
                 <h2 className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-black leading-tight">FREQUENTLY ASKED QUESTIONS</h2>
               </div>
               <div className="space-y-6">
                 {[
                   "What Is The Design + Build Process And How Long Does It Take?",
                   "How Long Does A Typical Luxury Renovation Take?",
                   "Can You Work Within My Budget While Still Delivering High-End Results?",
                   "Do I Need An Designer For My Renovation, Or Does Your Team Handle The Design?",
                   "How Involved Will I Be During The Renovation Process?"
                 ].map((q, i) => (
                   <div key={i} className="border-b border-gray-100 pb-4 flex justify-between items-center group cursor-pointer hover:text-[#97afd4] transition-colors">
                     <span className="text-sm font-bold uppercase tracking-wider">{q}</span>
                     <span className="text-lg">+</span>
                   </div>
                 ))}
               </div>
            </div>
          </div>
        </section>

        {/* Instagram Grid Placeholder */}
        <section className="py-24 px-6 lg:px-12 bg-white">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-16 text-[#97afd4] uppercase text-center tracking-widest">Instagram</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {[
                "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1631679706909-1844bbd07221?auto=format&fit=crop&q=80",
                "https://images.unsplash.com/photo-1615529328331-f8917597711f?auto=format&fit=crop&q=80"
              ].map((url, i) => (
                <div key={i} className="aspect-square bg-gray-100 relative group overflow-hidden">
                  <img src={url} className="w-full h-full object-cover transition-all duration-500" alt={`Interior Design ${i + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form Section */}
        <section className="py-24 px-6 lg:px-12 bg-[#f9f9f9]">
          <div className="max-w-3xl mx-auto bg-white p-12 shadow-sm border border-gray-100">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-black uppercase text-center tracking-tight">READY TO <span style={{ color: '#97afd4' }}>TRANSFORM</span> YOUR SPACE?</h2>
            <p className="text-center text-[#60524C]/60 mb-12 uppercase text-[10px] font-bold tracking-[0.2em]">Let us guide you through a seamless design + build process that brings your vision to life with precision, quality, and efficiency. Ready to get started?</p>
            <form className="grid gap-6">
              <div className="grid md:grid-cols-2 gap-6">
                <input type="text" placeholder="First Name*" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
                <input type="text" placeholder="Last Name*" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <input type="tel" placeholder="Phone*" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
                <input type="email" placeholder="Email*" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <select className="p-4 border-b border-gray-200 text-gray-400 outline-none focus:border-[#97afd4] text-sm bg-transparent appearance-none">
                  <option>Project type*</option>
                  <option>Kitchen remodeling</option>
                  <option>Bathroom remodeling</option>
                  <option>Full home renovation</option>
                  <option>Interior Design</option>
                </select>
                <select className="p-4 border-b border-gray-200 text-gray-400 outline-none focus:border-[#97afd4] text-sm bg-transparent appearance-none">
                  <option>Budget*</option>
                  <option>Up to $50,000</option>
                  <option> $50,000 - $100,000</option>
                  <option>Over $100,000</option>
                </select>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <input type="text" placeholder="ZIP code*" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
                <div className="hidden md:block"></div>
              </div>
              <textarea placeholder="Please tell us about your project" rows={4} className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent"></textarea>
              <input type="text" placeholder="Discount Code" className="p-4 border-b border-gray-200 outline-none focus:border-[#97afd4] text-sm bg-transparent" />
              
              <div className="flex items-start gap-4 py-4">
                <input type="checkbox" className="mt-1 accent-black" id="consent" />
                <label htmlFor="consent" className="text-[9px] text-gray-500 leading-relaxed uppercase tracking-widest font-bold cursor-pointer">
                  By clicking on the button you agree with the personal data processing policy
                </label>
              </div>
              
              <button className="bg-black text-white p-5 font-bold uppercase tracking-[0.3em] text-xs hover:bg-[#60524C] transition-all border border-black">GET STARTED TODAY</button>
            </form>
          </div>
        </section>
      </main>

      <footer className="py-20 px-6 lg:px-24 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 text-center md:text-left">
            <div className="col-span-1 md:col-span-2 space-y-8">
              <div className="border-2 border-white p-1 inline-block">
                <div className="text-lg font-black tracking-tight uppercase leading-none text-center">
                  PRISTINE INTERIORS<br/><span className="text-[8px] tracking-[0.4em] font-normal">DESIGN + BUILD</span>
                </div>
              </div>
              <p className="text-gray-400 text-xs leading-relaxed uppercase tracking-wider max-w-md">
                <span className="font-bold text-white">PRISTINE INTERIORS</span> is a premier firm specializing in luxury home renovations across the Dallas-Fort Worth (DFW) area. Through our seamless <span className="font-bold text-white">Design + Build</span> approach, we manage every aspect of your project—from initial concept to final completion—ensuring precision, efficiency, and exceptional craftsmanship.
              </p>
              <div className="flex justify-center md:justify-start">
                 <div className="border border-white/20 p-4 grayscale opacity-50">
                    <div className="text-[10px] font-bold tracking-widest uppercase">ACCREDITED<br/>BUSINESS</div>
                 </div>
              </div>
            </div>
            
            <div className="space-y-6">
              <h4 className="font-bold uppercase tracking-widest text-xs">CONTACT</h4>
              <ul className="text-gray-400 text-xs space-y-4 font-bold uppercase tracking-widest">
                <li className="flex items-center justify-center md:justify-start gap-2">
                   <span className="text-white">+1 (214) 843-4550</span>
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2">
                   <span className="text-white">eva@pristinehomeinteriors.com</span>
                </li>
                <li className="flex items-center justify-center md:justify-start gap-2">
                   <span className="text-white text-right md:text-left">3500 Oak Lawn Ave, Suite 460<br/>Dallas, TX 75219</span>
                </li>
              </ul>
            </div>

            <div className="space-y-6">
              <h4 className="font-bold uppercase tracking-widest text-xs">FOLLOW US</h4>
              <div className="flex justify-center md:justify-start gap-4 text-gray-400">
                <a href="#" className="hover:text-white transition-colors">FB</a>
                <a href="#" className="hover:text-white transition-colors">IG</a>
                <a href="#" className="hover:text-white transition-colors">LI</a>
                <a href="#" className="hover:text-white transition-colors">PI</a>
              </div>
            </div>
          </div>
          <div className="mt-20 pt-8 border-t border-white/10 text-center text-[10px] text-gray-500 uppercase tracking-[0.3em] font-bold">
            &copy; 2026 PRISTINE INTERIORS. ALL RIGHTS RESERVED.
          </div>
        </div>
      </footer>
    </div>
  );
}
